import React, { useEffect, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { cn } from '../../lib/utils';
import {
  slidesBotApi,
  SlidesTransactionReason,
  UserTransactionsResult,
} from '../../api/slidesBot';

interface Props {
  isDark: boolean;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string | null;
  userLabel: string;
}

// Display strings + colour cues for each transaction reason. Keeping
// English in the admin matches the rest of the panel (see ARCHITECTURE.md).
const REASON_META: Record<
  SlidesTransactionReason,
  { label: string; tone: 'pos' | 'neg' | 'neutral' }
> = {
  purchase: { label: 'Purchase', tone: 'pos' },
  generation: { label: 'Generation', tone: 'neg' },
  generation_refund: { label: 'Refund', tone: 'pos' },
  free_signup: { label: 'Signup bonus', tone: 'pos' },
  talabaa_link_bonus: { label: 'Link bonus', tone: 'pos' },
  admin_adjustment: { label: 'Admin adjust', tone: 'neutral' },
};

const REASON_OPTIONS: { value: SlidesTransactionReason | 'all'; label: string }[] = [
  { value: 'all', label: 'All reasons' },
  { value: 'purchase', label: 'Purchase' },
  { value: 'generation', label: 'Generation' },
  { value: 'generation_refund', label: 'Refund' },
  { value: 'free_signup', label: 'Signup bonus' },
  { value: 'talabaa_link_bonus', label: 'Link bonus' },
  { value: 'admin_adjustment', label: 'Admin adjust' },
];

const fmtDate = (s: string) => {
  const d = new Date(s);
  return (
    d.toLocaleDateString('ru-RU') +
    ' ' +
    d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
  );
};

const fmtDelta = (n: number) => (n > 0 ? `+${n}` : String(n));

export function UserTransactionsDialog({
  isDark,
  isOpen,
  onOpenChange,
  userId,
  userLabel,
}: Props) {
  const [data, setData] = useState<UserTransactionsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [reason, setReason] = useState<SlidesTransactionReason | 'all'>('all');
  const limit = 20;

  const fetchData = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await slidesBotApi.userTransactions(userId, {
        page,
        limit,
        reason: reason === 'all' ? undefined : reason,
      });
      setData(result);
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [userId, page, reason]);

  // Reset paging + refetch whenever the dialog is reopened for a new user
  // or filter changes.
  useEffect(() => {
    if (!isOpen) return;
    fetchData();
  }, [isOpen, fetchData]);

  useEffect(() => {
    setPage(1);
    setData(null);
  }, [userId, reason]);

  const totalPages = data ? Math.ceil(data.total / limit) : 1;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'max-w-4xl max-h-[90vh] overflow-y-auto',
          isDark ? 'bg-[#1E293B] border-slate-700' : 'bg-white',
        )}
      >
        <DialogHeader>
          <DialogTitle className={cn(isDark ? 'text-white' : 'text-[#0F172A]')}>
            Slide balance history
          </DialogTitle>
          <DialogDescription
            className={cn(isDark ? 'text-slate-400' : 'text-slate-500')}
          >
            {userLabel}
            {data && (
              <>
                {' · '}
                <span className="font-medium">
                  current balance: {data.current_balance} deck
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 mb-3">
          <Select
            value={reason}
            onValueChange={(v) => setReason(v as SlidesTransactionReason | 'all')}
          >
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {REASON_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {data && (
            <span className="text-xs text-slate-500 ml-auto">
              {data.total} total
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-[#0EA5A4]" />
          </div>
        ) : error ? (
          <p className="p-5 text-red-500">{error}</p>
        ) : !data || data.items.length === 0 ? (
          <p
            className={cn(
              'p-8 text-center',
              isDark ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            No transactions
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Δ</TableHead>
                <TableHead className="text-right">Balance after</TableHead>
                <TableHead>Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.items.map((t) => {
                const meta = REASON_META[t.reason];
                return (
                  <TableRow key={t.id}>
                    <TableCell className="text-xs text-slate-500">
                      {fmtDate(t.created_at)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-xs',
                          meta.tone === 'pos' && 'border-emerald-500 text-emerald-500',
                          meta.tone === 'neg' && 'border-amber-500 text-amber-500',
                        )}
                      >
                        {meta.label}
                      </Badge>
                    </TableCell>
                    <TableCell
                      className={cn(
                        'text-right font-mono font-medium',
                        t.delta > 0 ? 'text-emerald-500' : 'text-amber-500',
                      )}
                    >
                      {fmtDelta(t.delta)}
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {t.balance_after}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500 max-w-xs truncate">
                      {t.note ?? '—'}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {data && data.total > limit && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-xs text-slate-500">
              page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage((p) => p - 1)}
              >
                ← Prev
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next →
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
